import { useEffect, useState, type ReactNode } from 'react';
import { toMajor } from '../../contracts/money';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Pause,
  Phone,
  Plus,
  Star,
  Trash2,
  Wallet,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { Money } from '../../components/Money';
import { Tabs } from '../../components/Tabs';
import {
  GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT,
  GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT,
  useResellers,
  useResellerSubscriptions,
  useResellerPayments,
  useResellerPayouts,
  useResellerPayoutConfig,
  useResellerPackages,
  usePlatformPricingTiers,
  useResellerFloorOverrides,
  useResellerStaffAdmin,
  useAuditLog,
  setResellerStatus,
  startResellerImpersonation,
} from '../../api/admin';
import { useSession } from '../../stores/session';
import type {
  PlatformPricingTier,
  PlatformPricingTierSlug,
  ResellerFloorOverride,
  Reseller,
  ResellerPayoutAccount,
} from '../../mocks/admin';
import { useAdminResellerRevenue } from '../../api/admin';
import { usePackages } from '../../api/catalog';
import { formatDate, timeAgo } from '../../lib/format';
import { cn } from '../../lib/cn';

/** Reseller detail — header card + 8 tabs (Overview, Businesses, Staff, Branding, Domains, Packages, Revenue, Audit). */
export function ResellerDetail() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: resellers = [] } = useResellers();
  const { data: resellerPackages = [] } = useResellerPackages();
  const { data: resellerSubscriptions = [] } = useResellerSubscriptions();
  const { data: resellerPayments = [] } = useResellerPayments();
  const { data: resellerPayouts = [] } = useResellerPayouts();
  const { data: resellerStaff = [] } = useResellerStaffAdmin(id ?? '');
  const r = resellers.find((x) => x.id === id) ?? resellers[0];
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [openingAs, setOpeningAs] = useState(false);
  async function openAsReseller() {
    if (!r || openingAs) return;
    setOpeningAs(true);
    try {
      const { accessToken, target } = await startResellerImpersonation(r.id);
      useSession.getState().startImpersonation(accessToken, target, `/admin/resellers/${r.id}`);
      void navigate({ to: '/reseller' });
    } catch {
      setOpeningAs(false); // stay on the page on failure
    }
  }
  const [statusBusy, setStatusBusy] = useState(false);
  async function changeResellerStatus(status: 'active' | 'frozen') {
    if (!r || statusBusy) return;
    setStatusBusy(true);
    try {
      await setResellerStatus(r.id, status);
      void qc.invalidateQueries({ queryKey: ['admin', 'resellers'] });
    } finally {
      setStatusBusy(false);
    }
  }
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);

  if (!r) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/resellers"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All resellers
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <span
              className="w-12 h-12 rounded-lg flex-shrink-0 grid place-items-center text-base font-semibold text-white"
              style={{ background: r.primaryColor }}
            >
              {r.name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('')}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 truncate">
                  {r.name}
                </h1>
                <Badge tone={r.status === 'active' ? 'success' : r.status === 'frozen' ? 'error' : r.status === 'invited' ? 'warning' : 'neutral'}>
                  {r.status === 'active' ? 'Active' : r.status === 'frozen' ? 'Suspended' : r.status === 'invited' ? 'Invited' : 'Terminated'}
                </Badge>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400 mono">Slug: {r.slug}</span>
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                Owner: {r.ownerEmail} · Created {formatDate(r.createdAt)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" size="sm" iconLeft={<ExternalLink className="w-4 h-4" />} loading={openingAs} disabled={openingAs} onClick={openAsReseller}>
              Open as reseller
            </Button>
            {r.status === 'frozen' ? (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => changeResellerStatus('active')}>
                Reactivate
              </Button>
            ) : (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy || r.status === 'terminated'} onClick={() => setSuspendOpen(true)}>
                Suspend
              </Button>
            )}
            <Button variant="secondary" size="sm" iconLeft={<Pencil className="w-4 h-4" />}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" aria-label="More actions">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Commission % — variable per reseller, set by platform admin */}
        <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-zinc-700 grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-1">
              Platform commission
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-semibold text-zinc-950 dark:text-zinc-50 tabular-nums">
                {r.commissionPct}%
              </span>
              <button
                type="button"
                onClick={() => setCommissionOpen(true)}
                className="px-2 py-1 rounded-md text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition inline-flex items-center gap-1"
                title="Edit commission percentage"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            </div>
            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Variable per reseller · ITCity keeps {r.commissionPct}%, reseller keeps {100 - r.commissionPct}%
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-1">
              Catalog
            </div>
            <div className="text-2xl font-semibold text-zinc-950 dark:text-zinc-50 tabular-nums">
              {(resellerPackages.filter((p) => p.resellerId === r.id)).length} packages
            </div>
            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Reseller-owned · they set their own prices
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-1">
              Subdomain
            </div>
            <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mono truncate">
              {r.slug}.itcitysoft.com
            </div>
            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Customers sign up here at the reseller's prices
            </div>
          </div>
        </div>
      </Card>

      <SuspendResellerModal open={suspendOpen} onClose={() => setSuspendOpen(false)} reseller={r} />
      <CommissionModal open={commissionOpen} onClose={() => setCommissionOpen(false)} reseller={r} />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: <OverviewTab reseller={r} /> },
            { id: 'customers', label: 'Customers', content: <CustomersTab reseller={r} />, count: (resellerSubscriptions.filter((s) => s.resellerId === r.id)).length },
            { id: 'subscriptions', label: 'Subscriptions', content: <SubscriptionsTab reseller={r} />, count: (resellerSubscriptions.filter((s) => s.resellerId === r.id)).length },
            { id: 'payments', label: 'Payments', content: <PaymentsTab reseller={r} />, count: (resellerPayments.filter((p) => p.resellerId === r.id)).length },
            { id: 'payouts', label: 'Payouts', content: <PayoutsTab reseller={r} />, count: (resellerPayouts.filter((p) => p.resellerId === r.id)).length },
            { id: 'staff', label: 'Staff', content: <StaffTab resellerId={r.id} />, count: resellerStaff.length },
            { id: 'branding', label: 'Branding', content: <BrandingTab reseller={r} /> },
            { id: 'domains', label: 'Domains', content: <DomainsTab /> },
            { id: 'packages', label: 'Packages', content: <PackagesTab /> },
            { id: 'pricing-policy', label: 'Pricing policy', content: <PricingPolicyTab reseller={r} /> },
            { id: 'revenue', label: 'Revenue', content: <RevenueTab reseller={r} /> },
            { id: 'audit', label: 'Audit', content: <AuditTab reseller={r} /> },
          ]}
        />
      </Card>
    </div>
  );
}

type R = Reseller;

/* ── Suspend reseller modal ─────────────────────────────────────────── */

type SuspendReason = 'fraud' | 'compliance' | 'unpaid_fee' | 'partner_request' | 'other';

const SUSPEND_REASONS: Array<{ value: SuspendReason; label: string; payoutAction: 'hold' | 'forfeit' }> = [
  { value: 'fraud', label: 'Suspected fraud or abuse', payoutAction: 'forfeit' },
  { value: 'compliance', label: 'Compliance / KYC failure', payoutAction: 'hold' },
  { value: 'unpaid_fee', label: 'Unpaid platform fee or chargeback', payoutAction: 'hold' },
  { value: 'partner_request', label: 'Partner asked to pause', payoutAction: 'hold' },
  { value: 'other', label: 'Other', payoutAction: 'hold' },
];

function SuspendResellerModal({
  open,
  onClose,
  reseller: r,
}: {
  open: boolean;
  onClose: () => void;
  reseller: R;
}) {
  const { data: resellerSubscriptions = [] } = useResellerSubscriptions();
  const { data: resellerStaff = [] } = useResellerStaffAdmin(r.id);
  const { data: resellerRevenue } = useAdminResellerRevenue(r.id);
  const [reason, setReason] = useState<SuspendReason | null>(null);
  const [note, setNote] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const subs = resellerSubscriptions.filter((s) => s.resellerId === r.id);
  const activeSubs = subs.filter((s) => s.status === 'active' || s.status === 'trial' || s.status === 'past_due').length;
  const staff = resellerStaff.length;
  const heldPayout = resellerRevenue?.outstandingMinor ?? 0;
  const selectedReason = SUSPEND_REASONS.find((x) => x.value === reason);
  const payoutAction = selectedReason?.payoutAction ?? 'hold';
  const canConfirm = reason !== null && confirmText === 'SUSPEND';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Suspend reseller"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep active
          </Button>
          <Button
            variant="destructive"
            disabled={!canConfirm || busy}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await setResellerStatus(r.id, 'frozen');
                void qc.invalidateQueries({ queryKey: ['admin', 'resellers'] });
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Suspend reseller
          </Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        You're about to suspend <strong>{r.name}</strong>. This is reversible — you can reactivate
        at any time from this page.
      </p>

      {/* Impact summary */}
      <div className="mt-5 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
        <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-700">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            What changes immediately
          </div>
        </div>
        <ImpactRow
          tone="block"
          icon={<Lock className="w-4 h-4" />}
          title={`${staff} reseller staff lose portal access`}
          detail={`Reseller admins + staff at ${r.slug}.itcitysoft.com/admin will see a suspended message on their next login.`}
        />
        <ImpactRow
          tone="block"
          icon={<XCircle className="w-4 h-4" />}
          title="New customer signups under this reseller blocked"
          detail={`The branded signup flow at ${r.slug}.itcitysoft.com/signup redirects to the platform signup. Existing customers can still log in.`}
        />
        <ImpactRow
          tone="block"
          icon={<Pause className="w-4 h-4" />}
          title="Reseller-level webhooks + branding edits paused"
          detail="Branding stays in place for customer continuity. No staff invites, no domain changes, no package edits."
        />
        <ImpactRow
          tone="ok"
          icon={<CheckCircle2 className="w-4 h-4" />}
          title={`${activeSubs} customer business${activeSubs === 1 ? '' : 'es'} continue uninterrupted`}
          detail="Customers pay the platform directly. POS keeps working, subscriptions renew, invoices issue. Suspending the reseller never punishes a customer."
        />
        <ImpactRow
          tone="warn"
          icon={<Wallet className="w-4 h-4" />}
          title={
            payoutAction === 'forfeit' ? (
              <>Pending payout <Money minor={heldPayout} hideCents tone="neutral" className="font-semibold" /> <strong>forfeited</strong></>
            ) : (
              <>Pending payout <Money minor={heldPayout} hideCents tone="neutral" className="font-semibold" /> <strong>held pending review</strong></>
            )
          }
          detail={
            payoutAction === 'forfeit'
              ? "Fraud / abuse cases forfeit any unpaid commission per the partner agreement."
              : "Released on reactivation, or after compliance review concludes. Future commission accrual stops while suspended (100% to platform)."
          }
        />
      </div>

      {/* Reason picker */}
      <fieldset className="mt-5">
        <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
          Reason for suspension <span className="text-rose-600">*</span>
        </legend>
        <div className="space-y-2">
          {SUSPEND_REASONS.map((rs) => (
            <label
              key={rs.value}
              className={cn(
                'flex items-start gap-3 px-3 py-2 rounded-md border cursor-pointer transition',
                reason === rs.value
                  ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                  : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
              )}
            >
              <input
                type="radio"
                name="suspend-reason"
                value={rs.value}
                checked={reason === rs.value}
                onChange={() => setReason(rs.value)}
                className="mt-0.5 text-brand focus:ring-brand"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-900 dark:text-zinc-100">{rs.label}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {rs.payoutAction === 'forfeit'
                    ? 'Outstanding commission forfeited'
                    : 'Outstanding commission held pending review'}
                </div>
              </div>
            </label>
          ))}
        </div>
        {reason === 'other' && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Tell us a bit more (recorded in the audit log)…"
            rows={2}
            className="mt-3 block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition resize-none"
          />
        )}
      </fieldset>

      {/* Confirm input */}
      <div className="mt-5">
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
          Type <span className="mono font-semibold">SUSPEND</span> to confirm
        </label>
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="SUSPEND"
          className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition mono tabular-nums uppercase"
          autoComplete="off"
        />
        <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" />
          Action recorded in the audit log under <span className="mono">reseller.suspend</span> · {r.ownerEmail} will receive an email notice.
        </p>
      </div>
    </Modal>
  );
}

/* ── Edit commission modal ──────────────────────────────────────────── */

function CommissionModal({
  open,
  onClose,
  reseller: r,
}: {
  open: boolean;
  onClose: () => void;
  reseller: R;
}) {
  const [pct, setPct] = useState(r.commissionPct.toString());
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  const sample = 1000;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit commission percentage"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onClose}>Save</Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        Set the commission cut the platform keeps from every payment under{' '}
        <strong>{r.name}</strong>. The remainder is paid out to the reseller on their schedule.
      </p>

      <div className="mt-5">
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
          Platform commission %
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            className="flex-1 px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition tabular-nums"
            autoFocus
          />
          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">%</span>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
          Common values: 5% (high-volume partners), 15% (standard), 25% (new partners). Variable per reseller.
        </p>
      </div>

      <div className="mt-5 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
          Worked example · ৳1,000 payment
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-zinc-500 dark:text-zinc-400 text-xs">Customer pays</div>
            <Money minor={sample} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium block mt-0.5" />
          </div>
          <div>
            <div className="text-zinc-500 dark:text-zinc-400 text-xs">Platform keeps</div>
            <Money minor={Math.round(sample * (value / 100))} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200 font-medium block mt-0.5" />
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">{value}% commission</div>
          </div>
          <div>
            <div className="text-zinc-500 dark:text-zinc-400 text-xs">Reseller earns</div>
            <Money minor={Math.round(sample * (1 - value / 100))} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300 font-medium block mt-0.5" />
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">{100 - value}% net</div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" />
        Change applies to new payments only · existing accrued commission keeps the rate it was earned at · recorded in the audit log as <span className="mono">reseller.commission.update</span>
      </p>
    </Modal>
  );
}

function ImpactRow({
  tone,
  icon,
  title,
  detail,
}: {
  tone: 'block' | 'warn' | 'ok';
  icon: React.ReactNode;
  title: React.ReactNode;
  detail: string;
}) {
  const palette = {
    block: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30',
    warn: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30',
    ok: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30',
  }[tone];
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-t border-zinc-200 dark:border-zinc-700 first:border-t-0">
      <span className={cn('w-7 h-7 grid place-items-center rounded-md flex-shrink-0', palette)}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5 flex-wrap">
          {title}
        </div>
        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">{detail}</p>
      </div>
    </div>
  );
}

function OverviewTab({ reseller: r }: { reseller: R }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Businesses" value="128" sublabel="active subs 112" />
        <Stat label="MRR" value={<Money minor={19240000} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="12 month avg" />
        <Stat label="Commission YTD" value={<Money minor={38480000} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="20% of gross" />
        <Stat label="Outstanding" value={<Money minor={3850000} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="due 15 May" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Subscription mix" subtitle="Across all 128 businesses" />
          <div className="grid grid-cols-4 gap-3 text-center">
            <MiniStat n={112} label="Active" tone="emerald" />
            <MiniStat n={9} label="Trial" tone="amber" />
            <MiniStat n={5} label="Past due" tone="rose" />
            <MiniStat n={2} label="Suspended" tone="zinc" />
          </div>
        </Card>

        <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Branding preview" />
          <div className="flex items-center gap-4">
            <span
              className="w-16 h-16 rounded-lg grid place-items-center text-white font-semibold"
              style={{ backgroundColor: r.primaryColor }}
            >
              {r.name.charAt(0)}
            </span>
            <div className="text-sm">
              <div className="text-zinc-700 dark:text-zinc-200">Primary color</div>
              <div className="mono text-xs text-zinc-500 dark:text-zinc-400">{r.primaryColor}</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Recent activity" />
        <ul className="space-y-3 text-sm">
          {[
            { date: '03 May', text: 'Business added: Greenfields Mart' },
            { date: '01 May', text: 'Branding updated by rakib@acme-resellers.com' },
            { date: '28 Apr', text: 'Staff invited: nayem@acme-resellers.com' },
            { date: '25 Apr', text: 'Domain verified: acme-resellers.com' },
          ].map((a, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-14 flex-shrink-0 pt-0.5">{a.date}</span>
              <span className="text-zinc-700 dark:text-zinc-200">{a.text}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

const SUB_STATUS_TONE = {
  active: 'success' as const,
  trial: 'info' as const,
  past_due: 'warning' as const,
  suspended: 'error' as const,
  canceled: 'neutral' as const,
};

const SUB_STATUS_LABEL = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1">{label}</dt>
      <dd className="text-sm text-zinc-900 dark:text-zinc-100">{children}</dd>
    </div>
  );
}

function CustomersTab({ reseller: r }: { reseller: R }) {
  const { data: resellerSubscriptions = [] } = useResellerSubscriptions();
  const subs = resellerSubscriptions.filter((s) => s.resellerId === r.id);
  const [selected, setSelected] = useState<(typeof subs)[number] | null>(null);
  if (subs.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
        No customers yet. {r.name} hasn't signed anyone up.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Business</th>
            <th className="text-left font-medium px-4 py-3">Plan</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-right font-medium px-4 py-3">Amount</th>
            <th className="text-left font-medium px-6 py-3">Started</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {subs.map((s) => (
            <tr key={s.id} onClick={() => setSelected(s)} className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3">
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{s.businessName}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.ownerEmail}</div>
              </td>
              <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{s.packageName}</td>
              <td className="px-4 py-3">
                <Badge tone={SUB_STATUS_TONE[s.status]}>{SUB_STATUS_LABEL[s.status]}</Badge>
              </td>
              <td className="px-4 py-3 text-right">
                <Money minor={s.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">/{s.interval === 'month' ? 'mo' : 'yr'}</span>
              </td>
              <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(s.startedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.businessName} width="md">
        {selected && (
          <dl className="space-y-5">
            <Field label="Business">{selected.businessName}</Field>
            <Field label="Owner email">{selected.ownerEmail || '—'}</Field>
            <Field label="Plan">{selected.packageName}</Field>
            <Field label="Status"><Badge tone={SUB_STATUS_TONE[selected.status]}>{SUB_STATUS_LABEL[selected.status]}</Badge></Field>
            <Field label="Amount"><Money minor={selected.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" /> <span className="text-zinc-500 dark:text-zinc-400">/{selected.interval === 'month' ? 'mo' : 'yr'}</span></Field>
            <Field label="Started">{formatDate(selected.startedAt)}</Field>
            <Field label="Renews">{formatDate(selected.renewsAt)}</Field>
          </dl>
        )}
      </Drawer>
    </div>
  );
}

function SubscriptionsTab({ reseller: r }: { reseller: R }) {
  const { data: resellerSubscriptions = [] } = useResellerSubscriptions();
  const subs = resellerSubscriptions.filter((s) => s.resellerId === r.id);
  const active = subs.filter((s) => s.status === 'active').length;
  const trial = subs.filter((s) => s.status === 'trial').length;
  const pastDue = subs.filter((s) => s.status === 'past_due').length;
  const monthlyMrr = subs
    .filter((s) => s.status === 'active' && s.interval === 'month')
    .reduce((sum, s) => sum + s.amountMinor, 0);
  const yearlyArr = subs
    .filter((s) => s.status === 'active' && s.interval === 'year')
    .reduce((sum, s) => sum + s.amountMinor, 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total subs" value={subs.length} sublabel={`${active} active · ${trial} trial · ${pastDue} past due`} />
        <Stat label="Monthly MRR" value={<Money minor={monthlyMrr} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="from monthly billing only" />
        <Stat label="Annualized" value={<Money minor={yearlyArr} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="prepaid yearly subs" />
        <Stat label="Avg revenue/customer" value={<Money minor={subs.length ? Math.round((monthlyMrr + yearlyArr / 12) / subs.length) : 0} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="/month" />
      </div>

      {subs.length === 0 ? (
        <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
          No subscriptions yet for {r.name}.
        </div>
      ) : (
        <div className="overflow-x-auto nice-scroll -mx-6">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-6 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3">Package</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">Renews</th>
                <th className="text-left font-medium px-6 py-3">Started</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {subs.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-6 py-3">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">{s.businessName}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.ownerEmail}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{s.packageName}</td>
                  <td className="px-4 py-3">
                    <Badge tone={SUB_STATUS_TONE[s.status]}>{SUB_STATUS_LABEL[s.status]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Money minor={s.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">/{s.interval === 'month' ? 'mo' : 'yr'}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(s.renewsAt)}</td>
                  <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(s.startedAt, { compact: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PAY_STATUS_TONE = {
  paid: 'success' as const,
  failed: 'error' as const,
  refunded: 'neutral' as const,
};

const PAY_STATUS_LABEL = {
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
};

function PaymentsTab({ reseller: r }: { reseller: R }) {
  const { data: resellerPayments = [] } = useResellerPayments();
  const payments = resellerPayments.filter((p) => p.resellerId === r.id);
  const totalGross = payments.filter((p) => p.status === 'paid').reduce((s, p) => s + p.amountMinor, 0);
  const totalCommission = payments.filter((p) => p.status === 'paid').reduce((s, p) => s + p.commissionMinor, 0);
  const failed = payments.filter((p) => p.status === 'failed').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total payments" value={payments.length} sublabel={`${failed} failed`} />
        <Stat label="Gross collected" value={<Money minor={totalGross} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="paid customer invoices" />
        <Stat label="Commission earned" value={<Money minor={totalCommission} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel={`${r.commissionPct}% of gross`} />
        <Stat label="Platform share" value={<Money minor={totalGross - totalCommission} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel={`${100 - r.commissionPct}% to platform`} />
      </div>

      {payments.length === 0 ? (
        <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
          No payments collected yet under {r.name}.
        </div>
      ) : (
        <div className="overflow-x-auto nice-scroll -mx-6">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-6 py-3">Invoice</th>
                <th className="text-left font-medium px-4 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3">Method</th>
                <th className="text-right font-medium px-4 py-3">Gross</th>
                <th className="text-right font-medium px-4 py-3">Commission</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-6 py-3">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200">{p.invoiceNumber}</td>
                  <td className="px-4 py-3 text-zinc-900 dark:text-zinc-100">{p.businessName}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{p.method}</td>
                  <td className="px-4 py-3 text-right">
                    <Money minor={p.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Money minor={p.commissionMinor} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300 font-medium" />
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={PAY_STATUS_TONE[p.status]}>{PAY_STATUS_LABEL[p.status]}</Badge>
                  </td>
                  <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(p.receivedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PAYOUT_METHOD_LABEL = {
  bank_transfer: 'Bank transfer',
  bkash: 'bKash',
  nagad: 'Nagad',
  manual: 'Manual',
};

function PayoutsTab({ reseller: r }: { reseller: R }) {
  const { data: resellerPayouts = [] } = useResellerPayouts();
  const { data: resellerPayoutConfig = {} } = useResellerPayoutConfig();
  const { data: resellerRevenue } = useAdminResellerRevenue(r.id);
  const payouts = resellerPayouts.filter((p) => p.resellerId === r.id);
  const totalPaid = payouts.reduce((s, p) => s + p.amountMinor, 0);
  const cfg = resellerPayoutConfig[r.id];
  const accounts = cfg?.accounts ?? [];
  const defaultAccount = accounts.find((a) => a.isDefault);
  const activeAccounts = accounts.filter((a) => a.status === 'active');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat label="Total paid out" value={<Money minor={totalPaid} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel={`${payouts.length} payout${payouts.length === 1 ? '' : 's'}`} />
        <Stat label="Due now" value={<Money minor={resellerRevenue?.outstandingMinor ?? 0} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel={`min payout ৳${cfg?.minPayoutMinor ?? 1000} · ${cfg?.schedule === 'biweekly' ? 'every 15 days' : 'every 30 days'}`} />
        <Stat label="Avg payout" value={<Money minor={payouts.length ? Math.round(totalPaid / payouts.length) : 0} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="per period" />
      </div>

      {/* Saved payout accounts — multiple, one default */}
      <Card className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
              Payout accounts
              <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400 tabular-nums">
                ({activeAccounts.length} active{accounts.length > activeAccounts.length ? ` · ${accounts.length - activeAccounts.length} inactive` : ''})
              </span>
            </div>
            <div className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-300">
              Reseller can save multiple bKash numbers and bank accounts. The default is the
              destination admins use unless they pick another at transfer time.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" iconLeft={<Plus className="w-4 h-4" />}>
              Add account
            </Button>
            <Button size="sm" iconLeft={<Wallet className="w-4 h-4" />} disabled={!defaultAccount}>
              Record manual transfer
            </Button>
          </div>
        </div>

        {accounts.length === 0 ? (
          <div className="text-sm text-zinc-500 dark:text-zinc-400 py-4">
            Reseller hasn't saved any payout accounts yet — payouts paused until they add one.
          </div>
        ) : (
          <div className="space-y-2.5">
            {accounts.map((acc) => (
              <PayoutAccountRow key={acc.id} account={acc} resellerName={r.name} />
            ))}
          </div>
        )}
      </Card>

      {/* Manual-transfer instructions */}
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-2">
          How payouts are made
        </div>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Possignin does <strong>not</strong> use bKash B2B / Nagad B2B / instant bank-rail APIs.
          Payouts are <strong>manual transfers</strong> done by a platform admin on the
          {cfg?.schedule === 'biweekly' ? ' 1st and 15th' : ' 15th'} of each month, then recorded
          here for accounting. See{' '}
          <Link to="/admin/billing-flow" className="text-brand dark:text-brand-dm hover:underline font-medium">
            How billing works
          </Link>
          {' '}for the full flow.
        </p>
      </Card>

      {/* Payout history */}
      {payouts.length === 0 ? (
        <Card className="p-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
          No payouts recorded yet. Click <strong>Record manual transfer</strong> after sending the
          first one.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="px-5 sm:px-6 py-3 border-b border-zinc-200 dark:border-zinc-700 text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Payout history
          </div>
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-6 py-3">Period</th>
                  <th className="text-right font-medium px-4 py-3">Amount</th>
                  <th className="text-left font-medium px-4 py-3">Method</th>
                  <th className="text-left font-medium px-4 py-3">Reference</th>
                  <th className="text-left font-medium px-6 py-3">Recorded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {payouts.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-6 py-3 text-zinc-900 dark:text-zinc-100 tabular-nums">
                      {formatDate(p.periodStart, { compact: true })} – {formatDate(p.periodEnd, { compact: true })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Money minor={p.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">
                      <span className="inline-flex items-center gap-1.5">
                        <Wallet className="w-3.5 h-3.5 text-zinc-400" />
                        {PAYOUT_METHOD_LABEL[p.method]}
                      </span>
                    </td>
                    <td className="px-4 py-3 mono text-xs text-zinc-600 dark:text-zinc-300">{p.bankRef}</td>
                    <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(p.paidAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function FieldRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-0.5">{k}</dt>
      <dd className="text-sm text-zinc-900 dark:text-zinc-100">{v}</dd>
    </div>
  );
}

function PayoutAccountRow({
  account: acc,
  resellerName,
}: {
  account: ResellerPayoutAccount;
  resellerName: string;
}) {
  const inactive = acc.status === 'inactive';
  return (
    <div
      className={cn(
        'rounded-lg border p-4 transition',
        acc.isDefault
          ? 'border-brand bg-brand-subtle/40 dark:border-brand-dm dark:bg-brand-dm-subtle/20'
          : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
        inactive && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'w-10 h-10 grid place-items-center rounded-md flex-shrink-0',
            acc.type === 'bkash'
              ? 'bg-pink-100 dark:bg-pink-950/40 text-pink-700 dark:text-pink-300'
              : 'bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300',
          )}
        >
          {acc.type === 'bkash' ? <Phone className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-50 truncate">
              {acc.label}
            </span>
            {acc.isDefault && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
                <Star className="w-2.5 h-2.5 fill-current" />
                Default
              </span>
            )}
            {inactive && (
              <Badge tone="neutral" dot={false}>
                Inactive
              </Badge>
            )}
          </div>

          {acc.type === 'bkash' ? (
            <div className="mt-1.5 grid sm:grid-cols-3 gap-x-6 gap-y-1 text-xs">
              <span className="text-zinc-500 dark:text-zinc-400">
                Method · <span className="text-zinc-900 dark:text-zinc-100 font-medium">bKash personal</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                Account holder · <span className="text-zinc-900 dark:text-zinc-100">{acc.accountHolder}</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                Phone · <span className="mono text-zinc-900 dark:text-zinc-100">{acc.phone}</span>
              </span>
            </div>
          ) : (
            <div className="mt-1.5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
              <span className="text-zinc-500 dark:text-zinc-400">
                Method · <span className="text-zinc-900 dark:text-zinc-100 font-medium">Bank transfer</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                Bank · <span className="text-zinc-900 dark:text-zinc-100">{acc.bankName}</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                Branch · <span className="text-zinc-900 dark:text-zinc-100">{acc.branch}</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400 sm:col-span-2 lg:col-span-1">
                A/C name · <span className="text-zinc-900 dark:text-zinc-100">{acc.accountName}</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                A/C number · <span className="mono text-zinc-900 dark:text-zinc-100">{acc.accountNumber}</span>
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                Routing · <span className="mono text-zinc-900 dark:text-zinc-100">{acc.routingNumber}</span>
              </span>
            </div>
          )}

          <div className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
            Added {formatDate(acc.addedAt)} · {resellerName}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {!acc.isDefault && !inactive && (
            <button
              type="button"
              className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition whitespace-nowrap"
              title="Make this the default destination"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
          >
            Edit
          </button>
          <button
            type="button"
            aria-label={`More actions for ${acc.label}`}
            className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffTab({ resellerId }: { resellerId: string }) {
  const { data: resellerStaff = [] } = useResellerStaffAdmin(resellerId);
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Member</th>
            <th className="text-left font-medium px-4 py-3">Role</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-left font-medium px-6 py-3">Last sign-in</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {resellerStaff.map((s) => (
            <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3">
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{s.fullName}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.email}</div>
              </td>
              <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 capitalize">{s.role}</td>
              <td className="px-4 py-3">
                <Badge tone={s.status === 'active' ? 'success' : s.status === 'invited' ? 'info' : 'neutral'}>
                  {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                </Badge>
              </td>
              <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{s.lastLogin ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BrandingTab({ reseller: r }: { reseller: R }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Logo" subtitle="Used in reseller portal & emails" />
        <div className="flex items-center gap-4 p-6 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-dashed border-zinc-300 dark:border-zinc-700">
          <span
            className="w-16 h-16 rounded-lg grid place-items-center text-white text-xl font-semibold"
            style={{ backgroundColor: r.primaryColor }}
          >
            {r.name.charAt(0)}
          </span>
          <div className="text-sm">
            <div className="font-medium text-zinc-900 dark:text-zinc-100">acme-logo.svg</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">512 × 512 · 14 KB</div>
          </div>
        </div>
        <Button variant="secondary" size="sm" className="mt-4">Replace logo</Button>
      </Card>

      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Colors" />
        <div className="space-y-3">
          <ColorRow label="Primary" hex="#0F766E" />
          <ColorRow label="Accent" hex="#10B981" />
          <ColorRow label="Background" hex="#F9FAFB" />
        </div>
        <Button variant="secondary" size="sm" className="mt-4">Edit theme</Button>
      </Card>
    </div>
  );
}

function ColorRow({ label, hex }: { label: string; hex: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-700 dark:text-zinc-200">{label}</span>
      <span className="flex items-center gap-2">
        <span className="w-5 h-5 rounded border border-zinc-200 dark:border-zinc-700" style={{ background: hex }} />
        <span className="mono text-xs text-zinc-500 dark:text-zinc-400">{hex}</span>
      </span>
    </div>
  );
}

function DomainsTab() {
  return (
    <div className="space-y-3">
      {[
        { host: 'acme.itcitysoft.com', primary: true, verified: true, tls: 'issued' },
        { host: 'acme-resellers.com', primary: false, verified: true, tls: 'issued' },
        { host: 'pos.acme-stores.com', primary: false, verified: false, tls: 'pending' },
      ].map((d) => (
        <Card key={d.host} className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500">
                <Globe className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate flex items-center gap-2">
                  {d.host}
                  {d.primary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">CNAME · TLS {d.tls}</div>
              </div>
            </div>
            <Badge tone={d.verified ? 'success' : 'warning'}>
              {d.verified ? 'Verified' : 'Pending verification'}
            </Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}

function PackagesTab() {
  const { data: packages = [] } = usePackages();
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Package</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-right font-medium px-4 py-3">Subscribers</th>
            <th className="text-left font-medium px-6 py-3">Availability</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {packages.slice(0, 4).map((p) => (
            <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3">
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{p.name}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mono">{p.internalCode}</div>
              </td>
              <td className="px-4 py-3">
                <Badge tone={p.status === 'published' ? 'success' : p.status === 'draft' ? 'info' : 'warning'}>
                  {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.subscriberCount}</td>
              <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 capitalize">{p.soldBy.replace('_', ' ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Pricing policy tab ─────────────────────────────────────────────
 * Per-reseller floor overrides for each platform pricing tier. The
 * override is a *negotiated discount* off the platform default — the
 * reseller's packages can never go below this number. The tab is
 * pure mock state (no backend yet); save/delete simulate latency
 * then mutate `overrides` locally so the UI feels real.
 */

type FloorMap = Partial<Record<PlatformPricingTierSlug, ResellerFloorOverride>>;

function PricingPolicyTab({ reseller: r }: { reseller: R }) {
  const { data: resellerFloorOverrides } = useResellerFloorOverrides();
  const { data: platformPricingTiers = [] } = usePlatformPricingTiers();
  // Local copy — never mutate the fetched map
  const [overrides, setOverrides] = useState<FloorMap>({});
  const [editing, setEditing] = useState<{ tier: PlatformPricingTier; current?: ResellerFloorOverride } | null>(null);
  const [removing, setRemoving] = useState<{ tier: PlatformPricingTier; current: ResellerFloorOverride } | null>(null);

  // Seed local overrides once the fetched map lands. Keyed on the raw query
  // data (stable `undefined` until loaded) so this runs once.
  useEffect(() => {
    if (!resellerFloorOverrides) return;
    setOverrides({ ...(resellerFloorOverrides[r.id] ?? {}) });
  }, [resellerFloorOverrides, r.id]);

  const tiers = [...platformPricingTiers].sort((a, b) => a.sortOrder - b.sortOrder);

  function handleSave(slug: PlatformPricingTierSlug, next: ResellerFloorOverride) {
    setOverrides((prev) => ({ ...prev, [slug]: next }));
  }

  function handleRemove(slug: PlatformPricingTierSlug) {
    setOverrides((prev) => {
      const copy = { ...prev };
      delete copy[slug];
      return copy;
    });
  }

  // Recent floor changes — built from current overrides + a couple of plausible
  // historical (superseded) entries so the section never reads as empty.
  const recentChanges: Array<{
    id: string;
    at: string;
    tierLabel: string;
    monthly: number;
    yearly: number;
    actor: string;
    superseded?: boolean;
  }> = [];
  Object.entries(overrides).forEach(([slug, ovr]) => {
    if (!ovr) return;
    const tier = tiers.find((t) => t.slug === (slug as PlatformPricingTierSlug));
    if (!tier) return;
    recentChanges.push({
      id: `cur-${slug}`,
      at: ovr.setAt,
      tierLabel: tier.displayName,
      monthly: ovr.minMonthlyMinor,
      yearly: ovr.minYearlyMinor,
      actor: ovr.setByEmail,
    });
  });
  // A couple of plausible superseded history rows — only relevant if the
  // reseller has ever had an override, otherwise hide the whole section.
  if (recentChanges.length > 0) {
    recentChanges.push(
      {
        id: 'hist-1',
        at: '2026-03-28',
        tierLabel: 'Pro',
        monthly: 1150,
        yearly: 13800,
        actor: 'sayem@itcitysoft.com',
        superseded: true,
      },
      {
        id: 'hist-2',
        at: '2026-02-04',
        tierLabel: 'Pro',
        monthly: 1200,
        yearly: 14400,
        actor: 'sayem@itcitysoft.com',
        superseded: true,
      },
    );
  }
  recentChanges.sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader
          title={`Pricing policy — ${r.name}`}
          subtitle={
            <>
              Effective price floors that {r.name}'s packages can't go below.
              Modify per tier to give them a discount; leave blank to use the
              platform default. Global absolute floor:{' '}
              <span className="mono">৳{GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT}/mo</span>{' '}
              ·{' '}
              <span className="mono">৳{GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT}/yr</span>.
            </>
          }
        />

        <div className="overflow-x-auto nice-scroll -mx-6">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-6 py-3">Tier</th>
                <th className="text-left font-medium px-4 py-3">Platform default</th>
                <th className="text-left font-medium px-4 py-3">Override</th>
                <th className="text-left font-medium px-4 py-3">Effective</th>
                <th className="text-right font-medium px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {tiers.map((tier) => {
                const ovr = overrides[tier.slug];
                const effectiveMonthly = Math.max(
                  GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT,
                  tier.defaultMinMonthlyMinor,
                  ovr?.minMonthlyMinor ?? 0,
                );
                const effectiveYearly = Math.max(
                  GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT,
                  tier.defaultMinYearlyMinor,
                  ovr?.minYearlyMinor ?? 0,
                );
                const isOverridden = !!ovr;

                return (
                  <tr key={tier.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-6 py-3">
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">{tier.displayName}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">{tier.description}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">
                      <FloorAmount monthly={tier.defaultMinMonthlyMinor} yearly={tier.defaultMinYearlyMinor} muted />
                    </td>
                    <td className="px-4 py-3">
                      {isOverridden ? (
                        <FloorAmount monthly={ovr.minMonthlyMinor} yearly={ovr.minYearlyMinor} />
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FloorAmount monthly={effectiveMonthly} yearly={effectiveYearly} />
                        {isOverridden && (
                          <Badge tone="info" dot={false}>
                            Override
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing({ tier, current: ovr })}
                          className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition inline-flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" />
                          {isOverridden ? 'Edit' : 'Override'}
                        </button>
                        {isOverridden && (
                          <button
                            type="button"
                            onClick={() => setRemoving({ tier, current: ovr })}
                            aria-label={`Remove override for ${tier.displayName}`}
                            className="w-7 h-7 grid place-items-center rounded text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[11px] text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" />
          Floors apply on package save · existing reseller packages priced below
          a new floor are <strong>grandfathered</strong> until they are next
          edited · changes recorded as <span className="mono">reseller.floor.update</span>.
        </p>
      </Card>

      {recentChanges.length > 0 && (
        <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Recent floor changes" />
          <ul className="space-y-3 text-sm">
            {recentChanges.map((c) => (
              <li key={c.id} className="flex items-start gap-3">
                <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-20 flex-shrink-0 pt-0.5">
                  {c.at}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-900 dark:text-zinc-100">
                    <span className="font-medium">{c.tierLabel}</span> override{' '}
                    {c.superseded ? 'previously set' : 'added'} (
                    <span className="mono tabular-nums">
                      ৳{c.monthly.toLocaleString()} / ৳{c.yearly.toLocaleString()}
                    </span>
                    )
                    {c.superseded && (
                      <span className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
                        — superseded
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {c.actor}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {editing && (
        <FloorOverrideModal
          open
          tier={editing.tier}
          current={editing.current}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            handleSave(editing.tier.slug, next);
            setEditing(null);
          }}
        />
      )}

      {removing && (
        <RemoveOverrideModal
          open
          tier={removing.tier}
          onClose={() => setRemoving(null)}
          onConfirm={() => {
            handleRemove(removing.tier.slug);
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
}

function FloorAmount({
  monthly,
  yearly,
  muted = false,
}: {
  monthly: number;
  yearly: number;
  muted?: boolean;
}) {
  const cls = muted ? 'text-zinc-600 dark:text-zinc-300' : 'text-zinc-950 dark:text-zinc-50';
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <Money minor={monthly} hideCents tone="neutral" className={cls} />
      <span className="text-xs text-zinc-500 dark:text-zinc-400">/mo</span>
      <span className="text-zinc-300 dark:text-zinc-600">·</span>
      <Money minor={yearly} hideCents tone="neutral" className={cls} />
      <span className="text-xs text-zinc-500 dark:text-zinc-400">/yr</span>
    </span>
  );
}

function FloorOverrideModal({
  open,
  tier,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  tier: PlatformPricingTier;
  current?: ResellerFloorOverride;
  onClose: () => void;
  onSave: (next: ResellerFloorOverride) => void;
}) {
  const [monthly, setMonthly] = useState<string>(
    current ? current.minMonthlyMinor.toString() : tier.defaultMinMonthlyMinor.toString(),
  );
  const [yearly, setYearly] = useState<string>(
    current ? current.minYearlyMinor.toString() : tier.defaultMinYearlyMinor.toString(),
  );
  const [note, setNote] = useState(current?.note ?? '');
  const [saving, setSaving] = useState(false);

  const monthlyNum = Number(monthly) || 0;
  const yearlyNum = Number(yearly) || 0;

  const monthlyError =
    monthlyNum < GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT
      ? `Must be ≥ ৳${GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT} (global absolute floor)`
      : null;
  const yearlyError =
    yearlyNum < GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT
      ? `Must be ≥ ৳${GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT} (global absolute floor)`
      : null;
  const canSave = !monthlyError && !yearlyError && !saving;

  function handleSubmit() {
    if (!canSave) return;
    setSaving(true);
    setTimeout(() => {
      onSave({
        minMonthlyMinor: monthlyNum,
        minYearlyMinor: yearlyNum,
        setByEmail: 'sayem@itcitysoft.com',
        setAt: new Date().toISOString().slice(0, 10),
        note: note.trim() || undefined,
      });
      setSaving(false);
    }, 700);
  }

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={current ? `Edit ${tier.displayName} floor override` : `Override ${tier.displayName} floor`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSave} loading={saving}>
            {current ? 'Save changes' : 'Add override'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        Set a custom price floor for the <strong>{tier.displayName}</strong> tier
        for this reseller. Their packages mapped to this tier can't be priced
        below these numbers.
      </p>

      <div className="mt-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4 text-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
          Platform default for {tier.displayName}
        </div>
        <FloorAmount
          monthly={tier.defaultMinMonthlyMinor}
          yearly={tier.defaultMinYearlyMinor}
        />
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Monthly floor (BDT)
          </label>
          <input
            type="number"
            min={GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT}
            step={50}
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
            className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition tabular-nums mono"
            autoFocus
          />
          {monthlyError && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{monthlyError}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Yearly floor (BDT)
          </label>
          <input
            type="number"
            min={GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT}
            step={500}
            value={yearly}
            onChange={(e) => setYearly(e.target.value)}
            className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition tabular-nums mono"
          />
          {yearlyError && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{yearlyError}</p>
          )}
        </div>
      </div>

      <div className="mt-5">
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
          Note <span className="text-zinc-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="e.g. High-volume partner — agreed at quarterly review"
          className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition resize-none"
        />
      </div>

      <p className="mt-4 text-[11px] text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" />
        Recorded in the audit log as <span className="mono">reseller.floor.update</span>{' '}
        · existing packages below the new floor are grandfathered until edited.
      </p>

      {saving && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Saving floor override…
        </div>
      )}
    </Modal>
  );
}

function RemoveOverrideModal({
  open,
  tier,
  onClose,
  onConfirm,
}: {
  open: boolean;
  tier: PlatformPricingTier;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [removing, setRemoving] = useState(false);

  function handleRemove() {
    setRemoving(true);
    setTimeout(() => {
      onConfirm();
      setRemoving(false);
    }, 500);
  }

  return (
    <Modal
      open={open}
      onClose={removing ? () => {} : onClose}
      title="Remove floor override?"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={removing}>
            Keep override
          </Button>
          <Button variant="destructive" onClick={handleRemove} loading={removing}>
            Remove override
          </Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        Floor reverts to the platform default of{' '}
        <strong className="mono">৳{toMajor(tier.defaultMinMonthlyMinor).toLocaleString()}/mo</strong>
        {' '}/{' '}
        <strong className="mono">৳{toMajor(tier.defaultMinYearlyMinor).toLocaleString()}/yr</strong>{' '}
        for the <strong>{tier.displayName}</strong> tier.
      </p>
      <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
        Existing reseller packages priced below the platform default will be
        grandfathered until they are next edited.
      </p>
    </Modal>
  );
}

function RevenueTab({ reseller: r }: { reseller: R }) {
  const { data: resellerRevenue } = useAdminResellerRevenue(r.id);
  if (!resellerRevenue) {
    return (
      <div className="py-8 space-y-4" aria-busy="true">
        <div className="skeleton h-7 w-48 rounded" />
        <div className="space-y-3"><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Gross paid" value={<Money minor={resellerRevenue.grossPaidMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="this year" />
        <Stat label="Platform share" value={<Money minor={resellerRevenue.platformShareMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="80% to platform" />
        <Stat label="Commission" value={<Money minor={resellerRevenue.yourCommissionMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="20% to reseller" />
        <Stat label="Outstanding" value={<Money minor={resellerRevenue.outstandingMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />} sublabel="next payout 15 May" />
      </div>

      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Monthly commission" subtitle="Last 12 months" />
        <div className="flex items-end gap-2 h-32">
          {resellerRevenue.monthly.map((m) => {
            const max = Math.max(...resellerRevenue.monthly.map((x) => x.bdt));
            const pct = (m.bdt / max) * 100;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-brand/80 dark:bg-brand-dm/80 rounded-t-sm transition"
                  style={{ height: `${pct}%` }}
                />
                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate">{m.month.slice(0, 3)}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function AuditTab({ reseller: r }: { reseller: R }) {
  const { data: auditLog = [] } = useAuditLog();
  // Real platform audit feed. TODO(reseller-scoped): swap for GET /admin/resellers/:id/audit
  // once it exists, so this tab shows only events for this reseller.
  const events = auditLog.slice(0, 8);

  if (events.length === 0) {
    return (
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        No audit events recorded for {r.name} yet.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {events.map((a) => (
        <li key={a.id} className="flex items-start gap-3 text-sm">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5">
            <Calendar className="w-3.5 h-3.5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-zinc-900 dark:text-zinc-100">
              <span className="font-medium mono text-xs">{a.action}</span>
              <span className="text-zinc-500 dark:text-zinc-400"> · {a.target}</span>
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              {a.actor} · {timeAgo(a.occurredAt)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Stat({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: React.ReactNode;
  sublabel?: string;
}) {
  return (
    <Card className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-xl font-semibold text-zinc-950 dark:text-zinc-50">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{sublabel}</div>}
    </Card>
  );
}

function MiniStat({ n, label, tone }: { n: number; label: string; tone: 'emerald' | 'amber' | 'rose' | 'zinc' }) {
  const cls = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    rose: 'text-rose-700 dark:text-rose-300',
    zinc: 'text-zinc-700 dark:text-zinc-200',
  }[tone];
  return (
    <div className="text-center">
      <div className={`text-2xl font-semibold tabular-nums ${cls}`}>{n}</div>
      <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{label}</div>
    </div>
  );
}
